import { useState } from 'react';
import { SharedMediaLibrary } from '../components/MediaLibrary';
import { trpc } from '../lib/trpc';

export function SharedMediaTest() {
  const [selectedVenueId, setSelectedVenueId] = useState<string>('');
  const [message, setMessage] = useState<string>('');

  // Get venues for testing
  const { data: venues } = trpc.venue.list.useQuery({ limit: 10 });

  // Mutation for using shared media
  const useSharedMediaMutation = trpc.media.useSharedMedia.useMutation({
    onSuccess: (data: { action: string; mediaId: string; venueId: string; success: boolean }) => {
      setMessage(`Successfully ${data.action === 'setAsHero' ? 'set as hero image' : 'added to gallery'} for venue!`);
      setTimeout(() => setMessage(''), 3000);
    },
    onError: (error: { message: string }) => {
      setMessage(`Error: ${error.message}`);
      setTimeout(() => setMessage(''), 5000);
    },
  });

  const handleUseSharedMedia = (sharedMediaId: string, action: 'setAsHero' | 'addToGallery') => {
    if (!selectedVenueId) {
      setMessage('Please select a venue first');
      setTimeout(() => setMessage(''), 3000);
      return;
    }

    useSharedMediaMutation.mutate({
      sharedMediaId,
      venueId: selectedVenueId,
      action,
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Shared Media Library Test</h1>
          <p className="mt-2 text-gray-600">
            Test the shared media library functionality. Select a venue to test using shared media.
          </p>
        </div>

        {/* Venue Selection */}
        <div className="bg-gray-50 rounded-lg shadow p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Select Test Venue</h2>
          <div className="flex items-center space-x-4">
            <select
              value={selectedVenueId}
              onChange={(e) => setSelectedVenueId(e.target.value)}
              className="flex-1 max-w-md px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-info-500 focus:border-info-500"
            >
              <option value="">Choose a venue...</option>
              {venues?.items.map((venue) => (
                <option key={venue._id} value={venue._id}>
                  {venue.name}
                </option>
              ))}
            </select>
            {selectedVenueId && (
              <div className="text-sm text-green-600 font-medium">
                ✓ Venue selected
              </div>
            )}
          </div>
          {!selectedVenueId && (
            <p className="text-sm text-gray-500 mt-2">
              Select a venue to enable shared media actions (Set as Hero, Add to Gallery)
            </p>
          )}
        </div>

        {/* Status Message */}
        {message && (
          <div className={`mb-6 p-4 rounded-md ${
            message.startsWith('Error') 
              ? 'bg-red-50 border border-red-200 text-red-700'
              : 'bg-green-50 border border-green-200 text-green-700'
          }`}>
            {message}
          </div>
        )}

        {/* Shared Media Library */}
        <div className="bg-gray-50 rounded-lg shadow">
          <div className="p-6">
            <SharedMediaLibrary
              venueId={selectedVenueId || undefined}
              onUseSharedMedia={handleUseSharedMedia}
            />
          </div>
        </div>

        {/* Instructions */}
        <div className="mt-8 bg-primary-50 border border-primary-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-primary-900 mb-3">How to Test</h3>
          <ol className="list-decimal list-inside space-y-2 text-primary-800">
            <li>Select a venue from the dropdown above</li>
            <li>Browse the shared media library below</li>
            <li>Click on any shared media item to view details</li>
            <li>In the detail modal, use "Set as Hero Image" or "Add to Gallery" buttons</li>
            <li>The shared media will be referenced by the venue without copying the file</li>
            <li>Check the venue's page to see the shared media in use</li>
          </ol>
          <div className="mt-4 p-3 bg-primary-100 rounded-md">
            <p className="text-sm text-primary-700">
              <strong>Note:</strong> Shared media items are marked with a purple "Shared" badge 
              and cannot be deleted or have their publication status changed by venue managers.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}